import { useContext, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRuns, updateRun, patchRun, deleteRun, reprocessRuns } from '../api/runs';

import { getConfig } from '../api/configs';
import { effectiveCalibration } from '../utils/effectiveCalibration';
import { isRunActivelySettling } from '../types/Run';
// Straight from the context module, never via the provider: the provider
// imports this file's hooks, so going through it would close the cycle the
// reprocessContext split exists to break.
import { ReprocessContext } from '../providers/reprocessContext';

const SETTLING_POLL_MS = 5000;
// Watchdog: stop polling if the same set of settling runs has not changed for
// this long. Any real pipeline advance (a run leaving the set, or a new one
// joining) resets it, so this only ever fires on genuinely stuck runs — a
// PROCESSING row left behind by a crashed worker, say — instead of leaving
// every open tab refetching the full list forever.
const SETTLING_POLL_MAX_MS = 10 * 60 * 1000;

export const useRuns = (queryString: string = '') => {
  // Optional: RunList and friends render inside ReprocessProvider, but the hook
  // must work without it too.
  const reprocess = useContext(ReprocessContext);
  const pollSinceRef = useRef<{ key: string; since: number } | null>(null);

  return useQuery({
    queryKey: ['runs', queryString],
    queryFn: () => getRuns(queryString),
    // Poll while a listed run is actively settling (its run_ts is the
    // year-9999 creation sentinel until the end-of-processing save) so new
    // runs pick up their real timestamp without a manual refresh. Runs the
    // ReprocessProvider is already watching are excluded — it polls the
    // lightweight /runs/progress endpoint for them and invalidates ['runs']
    // when they finish, so a full-list poll alongside it is pure duplication.
    refetchInterval: (query) => {
      const runs = query.state.data;
      if (!Array.isArray(runs)) return false;
      const pollable = runs
        .filter((r: any) => isRunActivelySettling(r?.run_status) && !reprocess?.isRunReprocessing(r?.id))
        .map((r: any) => r?.id)
        .sort()
        .join(',');
      if (!pollable) {
        pollSinceRef.current = null;
        return false;
      }
      const now = Date.now();
      if (pollSinceRef.current?.key !== pollable) pollSinceRef.current = { key: pollable, since: now };
      return now - pollSinceRef.current.since > SETTLING_POLL_MAX_MS ? false : SETTLING_POLL_MS;
    },
  });
};

export const useRunAndCalibration = (runId: string) =>
  useQuery({
    queryKey: ['runAndCalibration', runId],
    queryFn: async () => {
      const run = await getRuns(`scope=grid_data_basic_plot&id=${runId}`).then((data) => data[0]);
      const rawCalibration = run.config_id ? await getConfig(run.config_id) : null;
      // Synthetic-DP runs must not have the pitot correction applied — serve
      // every consumer a per-run calibration view with cp_corr forced to 1.
      return { run, calibration: effectiveCalibration(rawCalibration, run) };
    },
    enabled: !!runId,
  });

/**
 * Gridded data filtered to the Aux Sensor comparison channels
 * (`grid_data_aux_sensor` scope): primary/secondary power + wheel speed on the
 * shared TimeRel_tG00 axis. Separate from the basic-plot fetch so that scope's
 * payload stays lean. Returns the run record (read `data_gridded` off it).
 */
export const useRunAuxSensorData = (runId: string | null) =>
  useQuery({
    queryKey: ['runAuxSensorData', runId],
    queryFn: () => getRuns(`scope=grid_data_aux_sensor&id=${runId}`).then((data) => data[0]),
    enabled: !!runId,
  });

export const useUpdateRun = () =>
  useMutation({
    mutationFn: (data: any) => updateRun(data.runId, data),
  });

/**
 * Partial update. `runId` is stripped from the body: it is routing, not a
 * column, and under PATCH the set of keys sent decides what gets written.
 */
export const usePatchRun = () =>
  useMutation({
    mutationFn: ({ runId, ...body }: any) => patchRun(runId ?? body.id, body),
  });

export const useDeleteRun = (runId: string) =>
  useMutation({
    mutationFn: () => deleteRun(runId),
  });

export const useReprocessRuns = () => useMutation({ mutationFn: reprocessRuns });

export const useInvalidateRuns = (queryString: string = '') => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['runs', queryString] });
  };
};
